import { useState } from 'react';
import { collection, getFirestore, query, where, orderBy } from "firebase/firestore";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

// Option constants for label lookups
const CAUTION_STATEMENT_OPTIONS = [
  { id: 0, label: 'None' },
  { id: 1, label: 'High NROI People' },
  { id: 2, label: 'High NROI People and Ctrl' },
  { id: 3, label: 'Minor Avoidant' },
  { id: 4, label: 'High Avoidant' }
];

const SEXUAL_ATTRACTION_OPTIONS = [
  { id: 1, label: 'Low' },
  { id: 2, label: 'Moderate' },
  { id: 3, label: 'High' }
];

const DATA_OPTIONS = [
  { id: 1, label: 'Below Normal' },
  { id: 2, label: 'Normal' },
  { id: 3, label: 'Above Normal' }
];

const HIGH_SEXUAL_OPTIONS = [
  { id: 0, label: 'None' },
  { id: 1, label: 'Above Normal' },
  { id: 2, label: 'Significantly Above Normal' }
];

const DISCRIMINATORY_OPTIONS = [
  { id: 0, label: 'None' },
  { id: 1, label: 'Yes' },
  { id: 2, label: 'Yes, Higher than all other groups' }
];

function getOptionLabel(options, id) {
  if (id === null || id === undefined) return '—';
  const option = options.find(opt => opt.id === id);
  return option ? `${option.label} (${option.id})` : '—';
}

function formatTimestamp(timestamp) {
  if (!timestamp) return '—';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

function ReportCard({ report, index }) {
  const [expanded, setExpanded] = useState(false);
  const results = report.results || {};

  const categories = [
    { id: 'femaleAdult', name: 'Female Adult', hasD: false },
    { id: 'maleAdult', name: 'Male Adult', hasD: false },
    { id: 'femaleAdolescent', name: 'Female Adolescent', hasD: false },
    { id: 'maleAdolescent', name: 'Male Adolescent', hasD: false },
    { id: 'femaleChild', name: 'Female Child', hasD: true },
    { id: 'maleChild', name: 'Male Child', hasD: true }
  ];

  return (
    <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between text-left"
      >
        <div>
          <span className="font-medium text-gray-900">Report #{index + 1}</span>
          <div className="text-sm text-gray-600 mt-1">
            <span>Submitted: {formatTimestamp(report.submittedAt)}</span>
            {report.scorer && (
              <span className="ml-4">Scorer: {report.scorer}</span>
            )}
          </div>
        </div>
        {expanded ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {expanded && (
        <div className="px-4 py-4 bg-white">
          {/* Metadata */}
          <div className="mb-4 text-sm text-gray-600">
            {report.caseNum && <div>Case Number: {report.caseNum}</div>}
            {report.source && <div>Source: {report.source}</div>}
          </div>

          {/* Results Table */}
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full text-sm border border-gray-200 rounded">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Category</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">SA</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">DA</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">HS</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">D</th>
                </tr>
              </thead>
              <tbody>
                {/* Caution Statement Row */}
                <tr className="border-b">
                  <td className="px-3 py-2 font-medium text-gray-900">Caution Statement</td>
                  <td colSpan={4} className="px-3 py-2 text-gray-600">
                    {getOptionLabel(CAUTION_STATEMENT_OPTIONS, results.cautionStatement)}
                  </td>
                </tr>
                {/* Category Rows */}
                {categories.map((cat) => (
                  <tr key={cat.id} className="border-b">
                    <td className="px-3 py-2 font-medium text-gray-900">{cat.name}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {getOptionLabel(SEXUAL_ATTRACTION_OPTIONS, results[cat.id]?.sexualAttraction)}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {getOptionLabel(DATA_OPTIONS, results[cat.id]?.data)}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {getOptionLabel(HIGH_SEXUAL_OPTIONS, results[cat.id]?.highSexual)}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {cat.hasD ? getOptionLabel(DISCRIMINATORY_OPTIONS, results[cat.id]?.discriminatory) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary Statement */}
          {report.summaryStatement && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Summary Statement</h4>
              <div className="bg-gray-50 rounded p-3 text-sm text-gray-700 whitespace-pre-wrap">
                {report.summaryStatement}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * View past reports generated for this evaluation
 * @param evalId
 * @constructor
 */
export default function ViewReports({ evalId }) {
  const collectionRef = collection(getFirestore(), 'evaluation_results');

  const [values, loading, error] = useCollectionData(
    query(
      collectionRef,
      where('evalId', '==', evalId),
      orderBy('submittedAt', 'desc')
    )
  );

  if (loading) {
    return <p className="text-gray-500">Loading reports...</p>;
  }

  if (error) {
    return <p className="text-red-500">Error loading reports: {error.message}</p>;
  }

  if (!values || values.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No reports have been submitted for this evaluation yet.</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Submitted Reports ({values.length})
      </h3>
      {values.map((report, index) => (
        <ReportCard key={index} report={report} index={index} />
      ))}
    </div>
  );
}
